import { useEffect, useRef, useState } from "react";
import { X, Maximize2, RefreshCcw, Info } from "lucide-react";
import "@/components/WorkflowModal.css";

const WORKFLOW_ASSETS = {
  classic: [
    {
      label: "Deine Auswahl",
      src: "/classic-selection.webp",
      alt: "Dreigeteilte TattooGen-Auswahl als Ausgangspunkt für die Nudelmann-Figur",
    },
    {
      label: "Schneller Sketch",
      src: "/classic-sketch.webp",
      alt: "Schnelle Bleistiftskizze der Nudelmann-Figur",
    },
    {
      label: "Handzeichnung",
      src: "/classic-handdrawing.webp",
      alt: "Handkolorierte Ausarbeitung der Nudelmann-Figur",
    },
    {
      label: "Fertige Komposition",
      src: "/classic-final.webp",
      alt: "Fertige farbige Oldschool-Komposition der Nudelmann-Figur",
      variantLabel: "Schwarz-Grau",
      variantSrc: "/classic-final-gray.webp",
      variantAlt: "Fertige Schwarz-Grau-Variante der Nudelmann-Figur",
    },
  ],
  illustrative: [
    {
      label: "Deine Auswahl",
      src: "/illustrative-selection.webp",
      alt: "Dreigeteilte TattooGen-Auswahl als Ausgangspunkt für den Kerzenmagier",
    },
    {
      label: "Schneller Sketch",
      src: "/illustrative-sketch.webp",
      alt: "Schneller rot-gelber Sketch des Kerzenmagiers",
    },
    {
      label: "Handzeichnung",
      src: "/illustrative-linework.webp",
      alt: "Ausgearbeitete Schwarz-Grau-Zeichnung des Kerzenmagiers",
    },
    {
      label: "Fertige Komposition",
      src: "/illustrative-final.webp",
      alt: "Fertige illustrative Schwarz-Grau-Komposition des Kerzenmagiers",
    },
  ],
};

function WorkflowImage({ item, onOpen }) {
  const [missing, setMissing] = useState(false);
  const [variantMissing, setVariantMissing] = useState(false);
  const [showVariant, setShowVariant] = useState(false);

  const primary = { ...item, variantSrc: undefined, variantAlt: undefined, variantLabel: undefined };
  const variant = item.variantSrc
    ? { label: `${item.label} · ${item.variantLabel}`, src: item.variantSrc, alt: item.variantAlt }
    : null;

  const imageButton = (displayItem, isVariant = false) => (
    <button
      type="button"
      className="workflow-image-button"
      onClick={() => onOpen(displayItem)}
      aria-label={`${displayItem.label} vergrößern`}
      style={item.variantSrc ? {
        position: "absolute",
        inset: 0,
        backfaceVisibility: "hidden",
        WebkitBackfaceVisibility: "hidden",
        transform: isVariant ? "rotateY(180deg)" : "rotateY(0deg)",
      } : undefined}
    >
      <img
        src={displayItem.src}
        alt={displayItem.alt}
        loading="lazy"
        onError={() => isVariant ? setVariantMissing(true) : setMissing(true)}
      />
      <span className="workflow-image-zoom" aria-hidden="true"><Maximize2 size={16} /></span>
    </button>
  );

  return (
    <article className="workflow-step-card">
      <div className="workflow-step-media">
        {!missing ? (
          item.variantSrc ? (
            <div
              className="workflow-flip-stage"
              style={{
                width: "100%",
                height: "100%",
                position: "relative",
                transformStyle: "preserve-3d",
                transition: "transform .5s ease",
                transform: showVariant ? "rotateY(180deg)" : "rotateY(0deg)",
              }}
            >
              {imageButton(primary, false)}
              {!variantMissing && variant ? imageButton(variant, true) : null}
            </div>
          ) : imageButton(primary)
        ) : (
          <div className="workflow-missing-asset" role="status">
            <span>{item.label}</span>
            <small>Bilddatei noch nicht eingebunden</small>
          </div>
        )}
      </div>
      <h4>{showVariant && variant ? `${item.label} · ${item.variantLabel}` : item.label}</h4>
      {item.variantSrc ? (
        <button
          type="button"
          className="workflow-variant-toggle"
          onClick={() => setShowVariant((value) => !value)}
          disabled={variantMissing}
          aria-pressed={showVariant}
        >
          <RefreshCcw size={14} /> {showVariant ? "Farbe ansehen" : "Schwarz-Grau ansehen"}
        </button>
      ) : null}
    </article>
  );
}

function WorkflowTransparencyDialog({ onClose }) {
  const closeRef = useRef(null);

  useEffect(() => {
    closeRef.current?.focus();
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div className="workflow-info-backdrop" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="workflow-info-dialog" role="dialog" aria-modal="true" aria-labelledby="workflow-info-title">
        <button ref={closeRef} type="button" className="workflow-info-close" onClick={onClose} aria-label="Info schließen">
          <X size={18} />
        </button>
        <span className="workflow-info-kicker">Transparenz</span>
        <h3 id="workflow-info-title">Was ist handgezeichnet – und was ist KI-gestützt?</h3>
        <p>
          TattooGen zeigt einen echten kreativen Arbeitsprozess. Dabei kombiniere ich je nach Motiv analoge Zeichnung, Procreate, digitale Bearbeitung und KI-gestützte Werkzeuge. Nicht jeder Schritt ist bei jedem Motiv gleich.
        </p>
        <div className="workflow-info-grid">
          <div>
            <strong>Von Hand</strong>
            <p>Schnelle Skizzen, Formfindung, Änderungen an Haltung und Proportionen sowie – bei ausgewählten Motiven – eine ausgearbeitete oder handkolorierte Originalzeichnung.</p>
          </div>
          <div>
            <strong>Digital / Procreate</strong>
            <p>Linien können bereinigt, Formen angepasst, Kontraste aufgebaut und einzelne Elemente für die spätere Tattoo-Umsetzung weiterentwickelt werden.</p>
          </div>
          <div>
            <strong>KI-gestützt</strong>
            <p>Einzelne Ausgangsbilder im Motivmischer und digitale Ausarbeitungen können mit KI-Unterstützung entstehen oder weiterentwickelt werden. Die gezeigten finalen Beispiele enthalten teilweise KI-gestützte Schritte.</p>
          </div>
        </div>
        <p className="workflow-info-bottom">
          Die Auswahl aus TattooGen ist deshalb nie das fertige Tattoo. Ich entscheide, welche Teile funktionieren, ändere die Komposition und bereite das Motiv individuell für Körperstelle, Größe und Tattoo-Umsetzung vor.
        </p>
      </section>
    </div>
  );
}

export default function WorkflowModal({ open, onClose, onRequest, triggerRef, hasActiveCombination }) {
  const dialogRef = useRef(null);
  const closeRef = useRef(null);
  const lightboxRef = useRef(null);
  const [lightboxItem, setLightboxItem] = useState(null);
  const [showTransparencyInfo, setShowTransparencyInfo] = useState(false);

  useEffect(() => {
    lightboxRef.current = lightboxItem;
  }, [lightboxItem]);

  useEffect(() => {
    if (!open) return undefined;

    const previousOverflow = document.body.style.overflow;
    const triggerNode = triggerRef?.current;
    document.body.style.overflow = "hidden";
    const focusTimer = window.setTimeout(() => closeRef.current?.focus(), 0);

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (showTransparencyInfo) setShowTransparencyInfo(false);
        else if (lightboxRef.current) setLightboxItem(null);
        else onClose();
        return;
      }

      if (event.key !== "Tab" || !dialogRef.current || showTransparencyInfo) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter((node) => !node.hasAttribute("hidden"));
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = previousOverflow;
      lightboxRef.current = null;
      window.setTimeout(() => triggerNode?.focus(), 0);
    };
  }, [open, onClose, triggerRef, showTransparencyInfo]);

  if (!open) return null;

  const handleBackdrop = (event) => {
    if (event.target === event.currentTarget) onClose();
  };

  const handleRequest = () => {
    onClose();
    window.setTimeout(onRequest, 0);
  };

  return (
    <div className="workflow-modal-backdrop" onMouseDown={handleBackdrop}>
      <section
        className="workflow-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="workflow-modal-title"
        aria-describedby="workflow-modal-intro"
        ref={dialogRef}
      >
        <button ref={closeRef} type="button" className="workflow-close" onClick={onClose} aria-label="Workflow schließen">
          <X size={20} />
        </button>

        <header className="workflow-modal-header">
          <span className="workflow-kicker">TattooGen · Arbeitsweise</span>
          <h2 id="workflow-modal-title">Vom Mix zum Tattoo</h2>
          <p id="workflow-modal-intro">
            Nicht jedes Motiv entsteht auf exakt demselben Weg. Ich kombiniere analoge und digitale Arbeitsschritte so, wie es für die jeweilige Figur und das spätere Tattoo sinnvoll ist.
          </p>
        </header>

        <div className="workflow-paths">
          <section className="workflow-path" aria-labelledby="workflow-classic-title">
            <div className="workflow-path-heading">
              <span>Weg 1</span>
              <h3 id="workflow-classic-title">Klar und Oldschool</h3>
              <p>Die ausgewählten Teile bleiben deutlich erkennbar, werden aber zu einer zusammenhängenden Figur verbunden und für eine klare, tattoo-taugliche Darstellung vereinfacht.</p>
            </div>
            <div className="workflow-steps">
              {WORKFLOW_ASSETS.classic.map((item) => <WorkflowImage key={item.label} item={item} onOpen={setLightboxItem} />)}
            </div>
            <p className="workflow-path-copy">Dieser Weg eignet sich besonders für klare, illustrative Figuren im Oldschool-Stil. Details werden bewusst reduziert, damit das Motiv auch als Tattoo lesbar bleibt. Die fertige Komposition kann je nach Wunsch farbig oder in Schwarz-Grau weiterentwickelt werden.</p>
          </section>

          <section className="workflow-path" aria-labelledby="workflow-illustrative-title">
            <div className="workflow-path-heading">
              <span>Weg 2</span>
              <h3 id="workflow-illustrative-title">Illustrativ und frei</h3>
              <p>Bei einer illustrativen Ausarbeitung darf sich die ursprüngliche Kombination stärker verändern. Haltung, Perspektive, Anatomie, Atmosphäre und Details werden freier entwickelt.</p>
            </div>
            <div className="workflow-steps">
              {WORKFLOW_ASSETS.illustrative.map((item) => <WorkflowImage key={item.label} item={item} onOpen={setLightboxItem} />)}
            </div>
            <p className="workflow-path-copy">Dieser Weg eignet sich für größere Motive mit mehr Tiefe, Bewegung und Atmosphäre. Der zusätzliche Ausarbeitungsaufwand wird individuell besprochen.</p>
          </section>
        </div>

        <aside className="workflow-note">
          <strong>Die gezeigten Wege sind Beispiele.</strong>
          <span>Welche Schritte sinnvoll sind, hängt von deiner Auswahl, der gewünschten Größe, der Körperstelle und dem Ausarbeitungsgrad ab.</span>
          <small>Eine handkolorierte Originalzeichnung kann bei ausgewählten Projekten zusätzlich entstehen.</small>
          <button type="button" className="workflow-info-trigger" onClick={() => setShowTransparencyInfo(true)}>
            <Info size={15} /> Handzeichnung & KI – wie entsteht das?
          </button>
        </aside>

        <div className="workflow-modal-actions">
          <button type="button" className="workflow-primary-cta" onClick={handleRequest}>
            {hasActiveCombination ? "Mit meiner Auswahl anfragen" : "Eigene Kombination starten"}
          </button>
        </div>

        {showTransparencyInfo ? <WorkflowTransparencyDialog onClose={() => setShowTransparencyInfo(false)} /> : null}

        {lightboxItem ? (
          <div className="workflow-lightbox" role="dialog" aria-modal="true" aria-label={`${lightboxItem.label} in großer Ansicht`} onMouseDown={(event) => event.target === event.currentTarget && setLightboxItem(null)}>
            <button type="button" className="workflow-lightbox-close" onClick={() => setLightboxItem(null)} aria-label="Große Bildansicht schließen"><X size={20} /></button>
            <img src={lightboxItem.src} alt={lightboxItem.alt} />
            <strong>{lightboxItem.label}</strong>
          </div>
        ) : null}
      </section>
    </div>
  );
}
